import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, PhoneCall } from 'lucide-react';
import { Alert, AuthLayout, Badge, Button, Card, CardContent, Input, Label, LoadingState, Select, ThemeToggle } from '@buzzbee/ui';
import { useEffect, useState } from 'react';
import { Logo } from '../components/common';
import { ApiError, errorMessage, get, post } from '../lib/api';
import { Link, useRouter } from '../lib/router';
import { useBrand, useRefreshMe } from '../lib/session';

type PublicPlan = { id: string; name: string; price: string; product: string; highlights: string[]; badge?: string };
type PublicPlans = { trialDays: number; overagePerMinute: string; products: { id: 'buzzbee' | 'receptionistbee'; name: string; tagline: string; plans: PublicPlan[] }[]; features: { id: string; label: string; plans: string[] }[] };

export function PricingPage() {
  useBrand('buzzbee');
  const plans = useQuery({ queryKey: ['public-plans'], queryFn: () => get<PublicPlans>('/api/public/plans') });
  return (
    <div className="min-h-dvh bg-background">
      <header className="mx-auto flex h-16 max-w-[1200px] items-center gap-3 px-4 sm:px-8">
        <Logo />
        <div className="ml-auto flex items-center gap-2">
          <ThemeToggle />
          <Button asChild variant="ghost" size="sm">
            <Link to="/sign-in">Sign in</Link>
          </Button>
          <Button asChild size="sm">
            <Link to="/sign-up?plan=receptionist">Start free trial</Link>
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-[1200px] px-4 pb-24 pt-10 sm:px-8">
        <section className="mx-auto max-w-3xl text-center">
          <Badge variant="primary">
            <PhoneCall className="size-3" /> AI receptionists for Canadian small businesses
          </Badge>
          <h1 className="mt-5 text-[40px] font-semibold leading-[1.05] tracking-[-0.035em] sm:text-[52px]">Never miss a call again.</h1>
          <p className="mx-auto mt-4 max-w-2xl text-[17px] text-muted-foreground">
            BuzzBee answers every call 24/7, takes messages and books appointments. ReceptionistBee is the premium front desk: multilingual voice, chat and SMS, reminders, no-show rescue, reviews and analytics.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">Prices in CAD per month. {plans.data ? `${plans.data.trialDays}-day free trial. Extra minutes ${plans.data.overagePerMinute} each.` : null}</p>
        </section>
        {plans.isLoading ? <LoadingState /> : null}
        {plans.data ? (
          <div className="mt-12 grid gap-10">
            {plans.data.products.map((product) => (
              <section key={product.id} data-brand={product.id}>
                <div className="mb-4 flex flex-wrap items-baseline gap-3">
                  <h2 className="text-2xl font-semibold tracking-[-0.02em]">{product.name}</h2>
                  <p className="text-muted-foreground">{product.tagline}</p>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {product.plans.map((p) => (
                    <Card key={p.id} className={p.badge ? 'ring-2 ring-primary' : ''}>
                      <CardContent className="flex h-full flex-col gap-4 p-7">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold">{p.name}</h3>
                          {p.badge ? <Badge variant="primary">{p.badge}</Badge> : null}
                        </div>
                        <p>
                          <span className="figures text-[36px] font-semibold tracking-[-0.03em]">{p.price}</span>
                          <span className="text-muted-foreground"> /month</span>
                        </p>
                        <ul className="flex-1 space-y-2 text-sm">
                          {p.highlights.map((h) => (
                            <li key={h} className="flex gap-2">
                              <Check className="mt-0.5 size-4 shrink-0 text-success" /> {h}
                            </li>
                          ))}
                        </ul>
                        <Button asChild variant={p.badge ? 'primary' : 'outline'}>
                          <Link to={`/sign-up?plan=${p.id}`}>Start {plans.data.trialDays}-day trial</Link>
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            ))}
            <section>
              <h2 className="mb-4 text-xl font-semibold">Compare features</h2>
              <Card className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="p-3 font-medium">Feature</th>
                      {plans.data.products.flatMap((pr) => pr.plans).map((p) => (
                        <th key={p.id} className="p-3 font-medium">
                          {p.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {plans.data.features.map((f) => (
                      <tr key={f.id} className="border-t">
                        <td className="p-3">{f.label}</td>
                        {plans.data.products.flatMap((pr) => pr.plans).map((p) => (
                          <td key={p.id} className="p-3">
                            {f.plans.includes(p.id) ? <Check className="size-4 text-success" aria-label="Included" /> : <span className="text-muted-foreground" aria-label="Not included">-</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </section>
          </div>
        ) : null}
      </main>
    </div>
  );
}

export function SignInPage() {
  useBrand(null);
  const refresh = useRefreshMe();
  const { navigate, search } = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const m = useMutation({
    mutationFn: () => post('/api/auth/signin', { email, password }),
    onSuccess: async () => {
      await refresh();
      navigate('/');
    },
  });
  const notVerified = m.error instanceof ApiError && m.error.code === 'EMAIL_NOT_VERIFIED';
  return (
    <AuthLayout logo={<Logo />} title="Sign in" description="BuzzBee and ReceptionistBee" footer={<>New here? <Link className="font-medium text-foreground underline" to="/pricing">See plans</Link></>}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        <div className="text-right">
          <Link className="text-sm text-muted-foreground underline" to="/forgot-password">
            Forgot password?
          </Link>
        </div>
        {search.get('reset') ? <Alert variant="success">Password updated. Sign in with your new password.</Alert> : null}
        {m.error ? <Alert variant={notVerified ? 'info' : 'destructive'}>{errorMessage(m.error)}</Alert> : null}
        <Button type="submit" className="w-full" loading={m.isPending}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}

export function SignUpPage() {
  const { search } = useRouter();
  const initialPlan = search.get('plan') ?? 'buzz_starter';
  const [form, setForm] = useState({ name: '', email: '', password: '', businessName: '', plan: initialPlan, vertical: 'general' });
  useBrand(form.plan.startsWith('receptionist') ? 'receptionistbee' : 'buzzbee');
  const packs = useQuery({ queryKey: ['packs'], queryFn: () => get<{ packs: { id: string; name: string }[] }>('/api/public/packs') });
  const m = useMutation({ mutationFn: () => post('/api/auth/signup', { ...form, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }) });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  if (m.isSuccess) {
    return (
      <AuthLayout logo={<Logo product={form.plan.startsWith('receptionist') ? 'receptionistbee' : 'buzzbee'} />} title="Check your email" description={`We sent a confirmation link to ${form.email}.`}>
        <p className="text-center text-sm text-muted-foreground">Open it to start your 7-day trial. (Running locally? The link is printed in the API console.)</p>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout logo={<Logo product={form.plan.startsWith('receptionist') ? 'receptionistbee' : 'buzzbee'} />} title="Start your free trial" description="7 days free. No card needed to start." footer={<>Have an account? <Link className="font-medium text-foreground underline" to="/sign-in">Sign in</Link></>}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="plan">Plan</Label>
          <Select
            id="plan"
            value={form.plan}
            onChange={set('plan')}
            options={[
              { value: 'buzz_starter', label: 'BuzzBee Starter ($99/mo)' },
              { value: 'buzz_growth', label: 'BuzzBee Growth ($249/mo)' },
              { value: 'receptionist', label: 'ReceptionistBee ($399/mo)' },
              { value: 'receptionist_pro', label: 'ReceptionistBee Pro ($899/mo)' },
            ]}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="businessName">Business name</Label>
          <Input id="businessName" value={form.businessName} onChange={set('businessName')} required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="vertical">Type of business</Label>
          <Select id="vertical" value={form.vertical} onChange={set('vertical')} options={(packs.data?.packs ?? [{ id: 'general', name: 'General appointments' }]).map((p) => ({ value: p.id, label: p.name }))} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="name">Your name</Label>
          <Input id="name" autoComplete="name" value={form.name} onChange={set('name')} required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" value={form.email} onChange={set('email')} required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password (10+ characters)</Label>
          <Input id="password" type="password" autoComplete="new-password" minLength={10} value={form.password} onChange={set('password')} required />
        </div>
        {m.error ? <Alert variant="destructive">{errorMessage(m.error)}</Alert> : null}
        <Button type="submit" className="w-full" loading={m.isPending}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}

export function VerifyEmailPage() {
  useBrand(null);
  const { search, navigate } = useRouter();
  const refresh = useRefreshMe();
  const token = search.get('token') ?? '';
  const m = useMutation({
    mutationFn: () => post('/api/auth/verify', { token }),
    onSuccess: async () => {
      await refresh();
      navigate('/onboarding', { replace: true });
    },
  });
  useEffect(() => {
    if (token && m.isIdle) m.mutate();
  }, [token, m]);
  return (
    <AuthLayout logo={<Logo />} title="Confirming your email">
      {m.error ? <Alert variant="destructive">{errorMessage(m.error)}</Alert> : <LoadingState label="Confirming" />}
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  useBrand(null);
  const [email, setEmail] = useState('');
  const m = useMutation({ mutationFn: () => post('/api/auth/password-reset/request', { email }) });
  return (
    <AuthLayout logo={<Logo />} title="Reset your password" description="We'll email you a link to set a new one." footer={<Link className="font-medium text-foreground underline" to="/sign-in">Back to sign in</Link>}>
      {m.isSuccess ? (
        <Alert variant="success" title="Check your email">If an account exists for {email}, a reset link is on its way. It works once and expires in 1 hour. (Running locally? It is printed in the API console.)</Alert>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            m.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="fp-email">Email</Label>
            <Input id="fp-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          {m.error ? <Alert variant="destructive">{errorMessage(m.error)}</Alert> : null}
          <Button type="submit" className="w-full" loading={m.isPending}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  useBrand(null);
  const { search, navigate } = useRouter();
  const token = search.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const m = useMutation({ mutationFn: () => post('/api/auth/password-reset/confirm', { token, password }), onSuccess: () => navigate('/sign-in?reset=1', { replace: true }) });
  const mismatch = confirm.length > 0 && confirm !== password;
  return (
    <AuthLayout logo={<Logo />} title="Choose a new password" footer={<Link className="font-medium text-foreground underline" to="/forgot-password">Request a new link</Link>}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!mismatch) m.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="rp-pw">New password (10+ characters)</Label>
          <Input id="rp-pw" type="password" autoComplete="new-password" minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rp-confirm">Confirm password</Label>
          <Input id="rp-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} aria-invalid={mismatch} required />
        </div>
        {mismatch ? <Alert variant="destructive">The passwords don't match.</Alert> : null}
        {m.error ? <Alert variant="destructive">{errorMessage(m.error)}</Alert> : null}
        <Button type="submit" className="w-full" loading={m.isPending} disabled={!token}>
          Set new password
        </Button>
      </form>
    </AuthLayout>
  );
}
